/**
 * NotificationSound utility for playing notification sounds with caching
 */

class NotificationSoundService {
  constructor() {
    this.audioCache = new Map();
    this.isEnabled = true;
    this.defaultSoundUrl = '/notification-sound.wav'; 
    this.fallbackSoundUrl = 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmAcBTGH0/LNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DyvmAcBTGH0/LNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DyvmAcBTGH0/LNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DyvmAcBTGH0/LNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DyvmAcBTGH0/LNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DyvmAcBTGH0/LNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DyvmAcBTGH0/LNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DyvmAcBTGH0/LNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DyvmAcBTGH0/LNeSsFJHfH8N2QQAoUXrTp66hVFApGn+DyvmAcBTGH0/LNeSsFJHfH8N2QQAoUXrTp66hVFA==';
    
    // Initialize audio context for better control
    this.initializeAudioContext();
  }

  initializeAudioContext() {
    try {
      // Create audio context for better audio handling
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
    } catch (error) {
      console.warn('NotificationSound: AudioContext not supported, falling back to HTML5 Audio');
      this.audioContext = null;
    }
  }

  /**
   * Load and cache audio file
   * @param {string} url - URL of the audio file
   * @returns {Promise<Audio>} - Cached audio object
   */
  async loadAndCacheAudio(url) {
    // Check if already cached
    if (this.audioCache.has(url)) {
      console.log('NotificationSound: Using cached audio for', url);
      return this.audioCache.get(url);
    }

    try {
      console.log('NotificationSound: Loading and caching audio from', url);
      
      // Create new audio object
      const audio = new Audio();
      
      // Set up audio properties
      audio.preload = 'auto';
      audio.volume = 0.7; // Set default volume to 70%
      
      // Load the audio
      await new Promise((resolve, reject) => {
        audio.addEventListener('canplaythrough', resolve, { once: true });
        audio.addEventListener('error', reject, { once: true });
        audio.src = url;
      });

      // Cache the audio object
      this.audioCache.set(url, audio);
      console.log('NotificationSound: Audio cached successfully for', url);
      
      return audio;
    } catch (error) {
      console.error('NotificationSound: Failed to load audio from', url, error);
      throw error;
    }
  }

  /**
   * Play notification sound
   * @param {string} soundUrl - Optional custom sound URL
   */
  async playNotificationSound(soundUrl = null) {
    if (!this.isEnabled) {
      console.log('NotificationSound: Sound is disabled');
      return;
    }

    try {
      const url = soundUrl || this.defaultSoundUrl;
      
      // Try to load and play the audio
      let audio;
      try {
        audio = await this.loadAndCacheAudio(url);
      } catch (error) {
        // Fallback to embedded sound if file not found
        console.warn('NotificationSound: Failed to load', url, ', trying fallback sound');
        audio = await this.loadAndCacheAudio(this.fallbackSoundUrl);
      }

      // Reset audio to beginning and play
      audio.currentTime = 0;
      
      // Play the sound
      const playPromise = audio.play();
      
      if (playPromise !== undefined) {
        await playPromise;
        console.log('NotificationSound: Played successfully');
      }
    } catch (error) {
      console.error('NotificationSound: Failed to play notification sound:', error);
      
      // Try browser notification sound as last resort
      this.playSystemBeep();
    }
  }

  /**
   * Play system beep as fallback
   */
  playSystemBeep() {
    try {
      if (this.audioContext) {
        // Create a simple beep using Web Audio API
        const oscillator = this.audioContext.createOscillator();
        const gainNode = this.audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(this.audioContext.destination);
        
        oscillator.frequency.setValueAtTime(800, this.audioContext.currentTime);
        gainNode.gain.setValueAtTime(0.1, this.audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.1);
        
        oscillator.start();
        oscillator.stop(this.audioContext.currentTime + 0.1);
        
        console.log('NotificationSound: Played system beep');
      }
    } catch (error) {
      console.error('NotificationSound: Failed to play system beep:', error);
    }
  }

  /**
   * Enable/disable notification sounds
   * @param {boolean} enabled 
   */
  setEnabled(enabled) {
    this.isEnabled = enabled;
    console.log('NotificationSound: Sound', enabled ? 'enabled' : 'disabled');
  }

  /**
   * Set volume for notification sounds
   * @param {number} volume - Volume level (0.0 to 1.0)
   */
  setVolume(volume) {
    const clampedVolume = Math.max(0, Math.min(1, volume));
    
    // Update volume for all cached audio
    this.audioCache.forEach(audio => {
      audio.volume = clampedVolume;
    });
    
    console.log('NotificationSound: Volume set to', clampedVolume);
  }

  /**
   * Clear audio cache
   */
  clearCache() {
    this.audioCache.clear();
    console.log('NotificationSound: Audio cache cleared');
  }

  /**
   * Preload notification sound for better performance
   */
  async preloadSound(soundUrl = null) {
    try {
      const url = soundUrl || this.defaultSoundUrl;
      await this.loadAndCacheAudio(url);
      console.log('NotificationSound: Sound preloaded successfully');
    } catch (error) {
      console.warn('NotificationSound: Failed to preload sound:', error);
    }
  }
}

// Create singleton instance
const notificationSound = new NotificationSoundService();

export default notificationSound;